/**
 * The footprint a contact shadow is drawn from, and the fringe built
 * off it — pure geometry, no renderer, so it can be checked in a unit
 * test. contactShadow.ts turns the result into a mesh; materialShadow.ts
 * decides which outline each material casts.
 */

/** A closed outline as a flat list of local-space points, x then y —
 * the shape `Graphics.poly` takes. Either winding. */
export type ShadowOutline = ReadonlyArray<number>;

/** The plain box outline — for pieces whose top really is one. */
export function rectOutline(
  x: number,
  y: number,
  width: number,
  height: number,
): number[] {
  return [x, y, x + width, y, x + width, y + height, x, y + height];
}

/** The outline stretched about the origin — a carried piece's grown
 * footprint, or the tumble's footprint between two stops. */
export function scaleOutline(
  outline: ShadowOutline,
  sx: number,
  sy: number,
): number[] {
  const out = new Array<number>(outline.length);
  for (let i = 0; i < outline.length; i += 2) {
    out[i] = outline[i]! * sx;
    out[i + 1] = outline[i + 1]! * sy;
  }
  return out;
}

/** How far a corner fan turns per triangle: a right-angled corner gets
 * six wedges, a wavy edge's few degrees of turn just one. */
const FAN_STEP_RAD = Math.PI / 12;

/** A concave corner's two fringe quads meet at a miter; past this many
 * spreads the miter is clipped, so a needle-sharp notch can't fling it. */
const MITER_LIMIT = 2;

export interface PenumbraGeometry {
  readonly positions: Float32Array;
  readonly uvs: Float32Array;
  readonly indices: Uint32Array;
}

/**
 * The fringe around an outline, `spread` px wide: a quad off each edge
 * along its outward normal and a fan around each convex corner, with
 * `u` running 0 at the outline to 1 at the rim (so a 1×N ramp texture
 * paints the falloff) and `v` a constant half. Concave corners miter
 * their two quads together so the fringe never doubles up on itself.
 */
export function penumbraGeometry(
  outline: ShadowOutline,
  spread: number,
): PenumbraGeometry {
  const pts = distinctPoints(outline);
  const n = pts.length;
  if (n < 3 || spread <= 0) {
    return {
      positions: new Float32Array(0),
      uvs: new Float32Array(0),
      indices: new Uint32Array(0),
    };
  }

  // Signed area picks which side is out: for a positive area the
  // outward normal of an edge lies to its right (dy, -dx), for a
  // negative one to its left — the same algebra either way up.
  let area = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    area += a.x * b.y - b.x * a.y;
  }
  const side = area >= 0 ? 1 : -1;
  const normals = pts.map((a, i) => {
    const b = pts[(i + 1) % n]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    return { x: (side * dy) / len, y: (side * -dx) / len };
  });
  // The corner is convex when the outline turns the way the whole
  // polygon winds: its two fringes diverge and a fan fills the gap.
  // Concave, they cross, and a miter joins them instead.
  const convex = (corner: number): boolean => {
    const before = normals[(corner + n - 1) % n]!;
    const after = normals[corner]!;
    return (before.x * after.y - before.y * after.x) * side >= 0;
  };

  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const vertex = (x: number, y: number, u: number): number => {
    positions.push(x, y);
    uvs.push(u, 0.5);
    return positions.length / 2 - 1;
  };

  // Where edge `edge`'s fringe ends at `corner`: straight out along
  // its own normal, or at the miter it shares with its neighbour
  const outerAt = (corner: number, edge: number): { x: number; y: number } => {
    const p = pts[corner]!;
    if (convex(corner)) {
      const own = normals[edge]!;
      return { x: p.x + own.x * spread, y: p.y + own.y * spread };
    }
    const before = normals[(corner + n - 1) % n]!;
    const after = normals[corner]!;
    const mx = before.x + after.x;
    const my = before.y + after.y;
    const mlen = Math.hypot(mx, my);
    // Half that length is the cosine of half the turn, and the miter
    // reaches spread over it
    const reach = Math.min(spread / (mlen / 2), spread * MITER_LIMIT);
    return { x: p.x + (mx / mlen) * reach, y: p.y + (my / mlen) * reach };
  };

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const a = pts[i]!;
    const b = pts[j]!;
    const ao = outerAt(i, i);
    const bo = outerAt(j, i);
    const v0 = vertex(a.x, a.y, 0);
    const v1 = vertex(b.x, b.y, 0);
    const v2 = vertex(bo.x, bo.y, 1);
    const v3 = vertex(ao.x, ao.y, 1);
    indices.push(v0, v1, v2, v0, v2, v3);
  }

  for (let i = 0; i < n; i++) {
    if (!convex(i)) continue;
    const before = normals[(i + n - 1) % n]!;
    const after = normals[i]!;
    const cross = before.x * after.y - before.y * after.x;
    const dot = Math.max(
      -1,
      Math.min(1, before.x * after.x + before.y * after.y),
    );
    const turn = Math.acos(dot);
    if (turn < 1e-6) continue;
    const p = pts[i]!;
    const steps = Math.max(1, Math.ceil(turn / FAN_STEP_RAD));
    const start = Math.atan2(before.y, before.x);
    const direction = cross >= 0 ? 1 : -1;
    const center = vertex(p.x, p.y, 0);
    let prev = vertex(p.x + before.x * spread, p.y + before.y * spread, 1);
    for (let k = 1; k <= steps; k++) {
      const angle = start + direction * turn * (k / steps);
      // The last rim point is the next edge's own, so the two meet
      // without a seam
      const rim =
        k === steps
          ? vertex(p.x + after.x * spread, p.y + after.y * spread, 1)
          : vertex(
              p.x + Math.cos(angle) * spread,
              p.y + Math.sin(angle) * spread,
              1,
            );
      indices.push(center, prev, rim);
      prev = rim;
    }
  }

  return {
    positions: new Float32Array(positions),
    uvs: new Float32Array(uvs),
    indices: new Uint32Array(indices),
  };
}

/** The outline's points with repeats dropped — a closing duplicate of
 * the first, or two the same in a row — which would make zero-length
 * edges with no normal. */
function distinctPoints(
  outline: ShadowOutline,
): ReadonlyArray<{ x: number; y: number }> {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i + 1 < outline.length; i += 2) {
    const x = outline[i]!;
    const y = outline[i + 1]!;
    const last = pts[pts.length - 1];
    if (last && last.x === x && last.y === y) continue;
    pts.push({ x, y });
  }
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (pts.length > 1 && first!.x === last!.x && first!.y === last!.y) {
    pts.pop();
  }
  return pts;
}
